import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { TEMPLATE_COMPONENTS } from "../components/builder/Templates";
import { Button } from "../components/ui/button";
import { Loader2, Lock, Printer, AlertCircle, FileText } from "lucide-react";

export default function SharedResume() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, resume: null, error: null, expiresAt: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.get(`${base}/api/share/${token}`);
        if (!cancelled) {
          setState({ loading: false, resume: data.resume, error: null, expiresAt: data.expires_at });
        }
      } catch (e) {
        if (!cancelled) {
          const status = e.response?.status;
          const detail = e.response?.data?.detail || "This link isn't available.";
          setState({
            loading: false,
            resume: null,
            error: { status, detail },
            expiresAt: null,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center" data-testid="shared-error">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900">
            {state.error.status === 410 ? "This link has expired" : "Link not available"}
          </h1>
          <p className="mt-2 text-slate-500">
            {state.error.detail}
          </p>
          <Link to="/">
            <Button className="mt-6 bg-slate-900 hover:bg-slate-800">
              Go to ResumeLock
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const Tpl = TEMPLATE_COMPONENTS[state.resume.template] || TEMPLATE_COMPONENTS.centered;
  const expires = state.expiresAt ? new Date(state.expiresAt) : null;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar — hidden in print */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 print:hidden">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <Lock className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-semibold text-sm text-slate-900 truncate" data-testid="shared-title">
                {state.resume.title || "Resume"}
              </div>
              <div className="text-[11px] text-slate-500">
                Shared via ResumeLock {expires ? `• link expires ${expires.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="shared-print-btn">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save PDF
            </Button>
            <Link to="/" className="hidden sm:block">
              <Button size="sm" variant="ghost" className="text-xs">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Make your own
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="py-8 px-4 print:py-0 print:px-0">
        <div className="max-w-[800px] mx-auto bg-white shadow-md rounded-md overflow-hidden print:shadow-none print:rounded-none" data-testid="shared-resume">
          <Tpl resume={state.resume} />
        </div>
        <div className="max-w-[800px] mx-auto mt-4 text-center text-[11px] text-slate-500 print:hidden">
          Read-only view • Owner has chosen to keep their references private.
        </div>
      </div>
    </div>
  );
}
